// @ts-nocheck
import { useEffect, useMemo } from 'react';
import { useDispatch } from 'react-redux';
import { Alert, Box, Typography } from '@mui/material';
import { addWeeks, differenceInWeeks, startOfWeek, subWeeks } from 'date-fns';
import moment from 'moment-timezone';

import {
  FCheckbox,
  FHAutocomplete,
  FHMUIDataTable,
  FHWeekDatesPicker,
} from '../../components/base';
import { CT_TIMEZONE_AREA, FacilityAssignmentType, Themes } from '../../constants';
import { FORTA_BLUE, FORTA_ERROR, FORTA_GRAY4, FORTA_WHITE } from '../../utils/colors';
import { ClinicalStatus } from '../../admin/constants/general';
import { AllowedServiceCodesToBuildClaim } from '../../components/layout/billings/claims/constants';
import { getAuthorTagName } from '../../components/layout/billings/claims/utils';
import { ClaimSessionTableColumns } from '../../components/layout/billings/claims/TableConfig';
import { ClaimSessionTableOptions } from '../../components/layout/billings/claims/TableRenderOptions';
import { clientActions } from '../../store';
import { useClaimBuilderSelectors } from './hooks/useClaimBuilderSelectors';
import ClaimBuilderPAAndPolicyDetails from '../../components/layout/billings/claims/ClaimBuilderPAAndPolicyDetails';
import {
  calculateUnitsLeft,
  filterActivePrimaryPoliciesForDateRange,
  filterCohortAssignmentsForDateRange,
  findFacilityForDateRange,
  getPolicyLabel,
  groupSessionsByBayClinicalRelationshipAndMonth,
} from './utils/general';

import type { HTMLAttributes, ChangeEvent } from 'react';
import type {
  IClient,
  IOption,
  IServiceCode,
  ISessionWithClaimStatus,
  IClientPolicyMap,
} from '../../utils/types';

const BAY_3 = '3';

interface IClaimBuilderProps {
  noOfSessions: number;
  numberOfUnitsSelected: number;
  isSelectedAll: boolean;
  bay2_6BillingWeekLag: number;
  billingLagInWeeks: number;
  weekPickerDate: Date;
  calendarDate: { startDate: Date; endDate: Date };
  sessionsLoading: boolean;
  hideClaimed: boolean;
  selectedPolicyDetails?: IClientPolicyMap;
  selectedTableKey: string | null;
  clients: IClient[];
  procedureCodes: IServiceCode[];
  selectedClient: IOption | null;
  filteredSessions: ISessionWithClaimStatus[];
  selectedProcedureCode: IServiceCode | null;
  selectedPolicy: IOption | null;
  setSelectedPolicy: (policy: IOption | null) => void;
  handleHideClaimed: (hideClaimed: boolean) => void;
  setWeekPickerDate: (date: Date) => void;
  setCalendarDate: (date: { startDate: Date; endDate: Date }) => void;
  setSelectedClient: (client: IOption | null) => void;
  handleSelectProcedure: (procedureCode: IServiceCode) => void;
  handleSelectAll: (
    selected: boolean,
    selectedTableKey: string,
    bay: string | null,
    clinicalRelationship: string | null,
    year: string | null,
    month: string | null,
    providerId: number | null | undefined,
  ) => void;
  handleSelectRow: (
    selected: boolean,
    session: ISessionWithClaimStatus,
    selectedTableKey: string,
    bay: string | null,
    clinicalRelationship: string | null,
    year: string | null,
    month: string | null,
    providerId: number | null | undefined,
  ) => void;
}

function ClaimBuilder({
  calendarDate,
  noOfSessions,
  sessionsLoading,
  hideClaimed,
  filteredSessions,
  isSelectedAll,
  bay2_6BillingWeekLag,
  billingLagInWeeks,
  weekPickerDate,
  clients,
  procedureCodes,
  selectedClient,
  selectedProcedureCode,
  selectedPolicy,
  selectedTableKey,
  numberOfUnitsSelected,
  selectedPolicyDetails,
  setSelectedPolicy,
  handleHideClaimed,
  setCalendarDate,
  setWeekPickerDate,
  setSelectedClient,
  handleSelectProcedure,
  handleSelectAll,
  handleSelectRow,
}: IClaimBuilderProps) {
  const dispatch = useDispatch();
  const {
    selectedClientFacility,
    selectedClientPlans,
    paDetailsLoading,
    selectedPolicyPriorAuthorizationDetails,
    providerLicensesLoading,
    providerPayorCredentialsLoading,
    clientCohortAssignments,
    clientDeatilsLoading,
  } = useClaimBuilderSelectors();

  const unitsLeft = useMemo(
    () => calculateUnitsLeft(selectedPolicyPriorAuthorizationDetails, numberOfUnitsSelected),
    [selectedPolicyPriorAuthorizationDetails, numberOfUnitsSelected],
  );

  const showClaimTable = Boolean(selectedClient && selectedProcedureCode);

  const weeklyActivePrimaryPolicies = useMemo(
    () => filterActivePrimaryPoliciesForDateRange(selectedClientPlans, calendarDate),
    [selectedClientPlans, calendarDate],
  );

  const policyOptions = useMemo(() => {
    return weeklyActivePrimaryPolicies.map((policy: IClientPolicyMap) => ({
      id: policy.id,
      label: getPolicyLabel(policy),
      description: `${policy.payor.label} - ${policy.payor.state}`,
    }));
  }, [weeklyActivePrimaryPolicies]);

  useEffect(() => {
    if (policyOptions.length === 1) {
      setSelectedPolicy(policyOptions[0]);
    } else if (policyOptions.length !== 1) {
      setSelectedPolicy(null);
    }
  }, [policyOptions, setSelectedPolicy]);

  const weeklyCohortAssignment = useMemo(
    () => filterCohortAssignmentsForDateRange(clientCohortAssignments, calendarDate),
    [clientCohortAssignments, calendarDate],
  );

  const sessionsByBayClinicalRelationshipAndMonth = useMemo(
    () => groupSessionsByBayClinicalRelationshipAndMonth(filteredSessions),
    [filteredSessions],
  );

  const [billingFacility, serviceLocation] = useMemo(
    () => [
      findFacilityForDateRange(
        selectedClientFacility,
        FacilityAssignmentType.BILLING,
        calendarDate,
      ),
      findFacilityForDateRange(
        selectedClientFacility,
        FacilityAssignmentType.SERVICE,
        calendarDate,
      ),
    ],
    [selectedClientFacility, calendarDate],
  );

  const isSelectedWeekWithinBillingLag = useMemo(() => {
    const weeksDiff = differenceInWeeks(new Date(), calendarDate.startDate);
    return weeksDiff >= 0 && weeksDiff < billingLagInWeeks;
  }, [calendarDate.startDate, billingLagInWeeks]);

  const modifierLabel = useMemo(() => {
    let label = '';
    if (selectedPolicyPriorAuthorizationDetails?.provider_modifier) {
      label += selectedPolicyPriorAuthorizationDetails?.provider_modifier;
    }
    if (selectedPolicyPriorAuthorizationDetails?.service_modifier) {
      label += label
        ? `, ${selectedPolicyPriorAuthorizationDetails?.service_modifier}`
        : selectedPolicyPriorAuthorizationDetails?.service_modifier;
    }
    return label;
  }, [selectedPolicyPriorAuthorizationDetails]);

  return (
    <Box>
      <Box sx={{ flexDirection: 'row', display: 'flex', mb: 3, gap: 3, height: 'auto' }}>
        <Box>
          <Box textAlign="left">
            <Typography
              sx={{ mr: 1, alignSelf: 'start', mb: 1, fontWeight: 700, color: FORTA_BLUE.primary }}
            >
              Client
            </Typography>
            <FHAutocomplete
              data-testid="clientDropdownId"
              sx={{ flexGrow: 1, minWidth: 300, boxShadow: 'none' }}
              inputHeight={40}
              colorTheme={Themes.Primary}
              coloredBackground
              placeholder="Select client"
              clearIcon={false}
              isOptionEqualToValue={(option: IOption, value: IOption) => option.id === value.id}
              renderOption={(props: HTMLAttributes<HTMLLIElement>, option: IOption) => {
                return (
                  <Box
                    {...props}
                    sx={{
                      display: 'flex !important',
                      justifyContent: 'space-between !important',
                    }}
                    key={option.id}
                    data-testid={`clientLabel${option.id}`}
                  >
                    <Typography>{option.label}</Typography>
                    {option.description && (
                      <Typography
                        sx={{
                          px: 0.7,
                          ml: 1,
                          borderRadius: '4px',
                          fontSize: 12,
                          fontWeight: 700,
                          color: FORTA_WHITE,
                          backgroundColor: FORTA_GRAY4,
                        }}
                      >
                        {option.description}
                      </Typography>
                    )}
                  </Box>
                );
              }}
              options={clients.map((client: IClient) => ({
                id: client.id,
                label: `${client.first_name} ${client.last_name}`,
                description:
                  client.clinical_status === ClinicalStatus.OFFBOARDED ||
                  client.clinical_status === ClinicalStatus.ON_HOLD
                    ? client.clinical_status
                    : '',
              }))}
              value={selectedClient}
              onChange={(_: ChangeEvent<HTMLSelectElement>, newValue: IOption | null) => {
                setSelectedClient(newValue);
                setSelectedPolicy(null);
                dispatch(clientActions.clearSelectedClient());
              }}
            />
          </Box>
          <Box mt={1} textAlign="left">
            <Typography
              sx={{ mr: 3, alignSelf: 'start', mb: 1, fontWeight: 700, color: FORTA_BLUE.primary }}
            >
              Procedure code
            </Typography>
            <FHAutocomplete
              data-testid="procedureCodeDropdownId"
              sx={{ flexGrow: 1, minWidth: 270, boxShadow: 'none' }}
              inputHeight={40}
              colorTheme={Themes.Primary}
              coloredBackground
              placeholder="Select procedure code"
              clearIcon={false}
              isOptionEqualToValue={(option: IOption, value: IOption) => option.id === value.id}
              renderOption={(props: HTMLAttributes<HTMLLIElement>, option: IServiceCode) => {
                return (
                  <li {...props} key={option.id} data-testid={`procedureCode${option.code}`}>
                    {`${option.label}`}
                  </li>
                );
              }}
              options={procedureCodes}
              value={selectedProcedureCode || ''}
              onChange={(_: ChangeEvent<HTMLSelectElement>, newValue: IServiceCode | null) => {
                if (!newValue) return;
                handleSelectProcedure(newValue);
              }}
            />
          </Box>

          <Box mt={1} textAlign="left">
            <Typography
              display="inline"
              sx={{ alignSelf: 'start', mb: 1, fontWeight: 700, color: FORTA_BLUE.primary }}
            >
              Policy
            </Typography>
            {policyOptions.length > 1 && (
              <Typography
                display="inline"
                fontWeight={'bold'}
                sx={{ alignSelf: 'start', mb: 1, color: FORTA_ERROR }}
              >
                {` - ${policyOptions.length} policies`}
              </Typography>
            )}
            <FHAutocomplete
              data-testid="policyDropdownId"
              sx={{ flexGrow: 1, minWidth: 270, boxShadow: 'none' }}
              inputHeight={40}
              colorTheme={Themes.Primary}
              coloredBackground
              disabled={!selectedClient || !selectedProcedureCode}
              placeholder="Select policy"
              clearIcon={false}
              isOptionEqualToValue={(option: IOption, value: IOption) => option.id === value.id}
              renderOption={(props: HTMLAttributes<HTMLLIElement>, option: IOption) => {
                return (
                  <li {...props} key={option.id} data-testid={`policy${option.id}`}>
                    {`${option.label}`}
                  </li>
                );
              }}
              options={policyOptions}
              value={selectedPolicy || ''}
              onChange={(_: ChangeEvent<HTMLSelectElement>, newValue: IOption | null) => {
                setSelectedPolicy(newValue);
              }}
            />
          </Box>
        </Box>
        <Box textAlign="left" flexGrow={1} height="100%">
          <Typography sx={{ mb: 1, fontWeight: 700, color: FORTA_BLUE.primary }}>
            PA and Policy Details
          </Typography>
          <ClaimBuilderPAAndPolicyDetails
            unitsLeft={unitsLeft}
            paDetailsLoading={paDetailsLoading}
            clientDeatilsLoading={clientDeatilsLoading}
            selectedClient={selectedClient}
            selectedProcedureCode={selectedProcedureCode}
            weeklyCohortAssignment={weeklyCohortAssignment}
            billingFacility={billingFacility}
            serviceLocation={serviceLocation}
            selectedPolicy={selectedPolicy}
            modifierLabel={modifierLabel}
            selectedPolicyPriorAuthorizationDetails={selectedPolicyPriorAuthorizationDetails}
          />
        </Box>
      </Box>
      {showClaimTable && selectedProcedureCode && (
        <Box
          sx={{
            borderRadius: 2,
            boxShadow: '0px 4px 4px 0px rgba(18, 78, 120, 0.25)',
          }}
        >
          <Box
            sx={{
              backgroundColor: FORTA_BLUE.light3,
              alignItems: 'center',
              width: '100%',
              p: 3,
            }}
          >
            <Box display={'flex'} flex={1} justifyContent={'center'}>
              <FHWeekDatesPicker
                handlerWidth={540}
                containerStyles={{ px: 1.5, py: 1 }}
                calendarPickerIconStyle={{
                  '& path': {
                    fill: FORTA_BLUE.primary,
                  },
                }}
                value={weekPickerDate}
                numberOfPastWeeksToDisable={
                  selectedProcedureCode?.code ===
                  AllowedServiceCodesToBuildClaim.SessionProcedureCode
                    ? Math.min(bay2_6BillingWeekLag, billingLagInWeeks)
                    : billingLagInWeeks
                }
                onChange={(date: Date, startDate: Date, endDate: Date) => {
                  setWeekPickerDate(date);
                  setCalendarDate({ startDate, endDate });
                }}
              />
            </Box>

            <Box display={'flex'} my={2} justifyContent={'space-between'} alignItems={'center'}>
              <Box>
                {isSelectedWeekWithinBillingLag &&
                  selectedProcedureCode?.code ===
                    AllowedServiceCodesToBuildClaim.SessionProcedureCode && (
                    <Typography sx={{ fontWeight: 700, color: FORTA_BLUE.primary }}>
                      Bay 3 session conducted within {billingLagInWeeks} weeks from today are not
                      eligible for billing.
                    </Typography>
                  )}
              </Box>
              <Box display={'flex'} alignItems={'center'}>
                <FCheckbox
                  sx={{ mr: 0 }}
                  iconStyles={{ width: '25px', height: '25px' }}
                  colorTheme={Themes.Primary}
                  inputProps={{ 'data-testid': 'hideClaimed' }}
                  disabled={sessionsLoading}
                  checked={hideClaimed}
                  onChange={(e) => handleHideClaimed(e.target.checked)}
                />
                <Typography sx={{ mr: 0.5 }}>Hide claimed</Typography>
              </Box>
            </Box>
            {filteredSessions.length > 0 &&
              selectedPolicyPriorAuthorizationDetails?.max_units_per_claim && (
                <Alert severity="info" sx={{ fontWeight: 700, mb: 2, alignItems: 'center' }}>
                  {`Max units per claim: ${selectedPolicyPriorAuthorizationDetails?.max_units_per_claim}`}
                </Alert>
              )}
            {sessionsByBayClinicalRelationshipAndMonth.sortedKeys.map((compositeKey) => {
              const {
                sessions,
                bay: bayValue,
                clinicalRelationship,
                monthKey,
                providerId,
                authorDisplayName,
              } = sessionsByBayClinicalRelationshipAndMonth.grouped[compositeKey];
              const monthDisplay = moment(`${monthKey}-01`).format('MMMM YYYY');
              const tableTitle =
                sessionsByBayClinicalRelationshipAndMonth.sortedKeys.length > 1
                  ? `${bayValue ? `Bay ${bayValue} - ` : ''}${
                      !clinicalRelationship
                        ? ''
                        : `${
                            getAuthorTagName(clinicalRelationship) ||
                            clinicalRelationship
                          } - `
                    }${authorDisplayName ? `${authorDisplayName} - ` : ''}${monthDisplay}`
                  : '';

              return (
                <Box key={compositeKey} sx={{ mb: 4 }}>
                  <Box
                    sx={{
                      backgroundColor: FORTA_WHITE,
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'space-between',
                      px: 4,
                      mx: 0.2,
                      borderRadius: '4px',
                    }}
                  >
                    {tableTitle && (
                      <Typography
                        sx={{
                          mb: 2,
                          mt: 4,
                          fontSize: 21,
                          fontWeight: 700,
                          color: FORTA_BLUE.primary,
                          textAlign: 'left',
                          width: '50%',
                          minHeight: '40px',
                        }}
                        data-testid={`bay-table-title-${compositeKey}`}
                      >
                        {tableTitle}
                      </Typography>
                    )}
                    <Box display="flex" alignItems="center" gap={2} sx={{ width: '50%', mt: 2 }}>
                      <Typography sx={{ fontWeight: 700, color: FORTA_BLUE.primary }}>
                        Sessions selected: {selectedTableKey === compositeKey ? noOfSessions : 0}
                      </Typography>
                      <Typography sx={{ fontWeight: 700, color: FORTA_BLUE.primary }}>
                        Units selected:{' '}
                        {selectedTableKey === compositeKey ? numberOfUnitsSelected : 0}
                      </Typography>
                    </Box>
                  </Box>

                  <FHMUIDataTable
                    containerStyles={{
                      display: 'flex',
                      flexDirection: 'column',
                      height: '100%',
                      margin: '0 2px',
                      padding: '16px',
                      boxShadow: 'none',
                      border: 'none',
                    }}
                    title=""
                    data={
                      bayValue === BAY_3 &&
                      selectedProcedureCode?.code ===
                        AllowedServiceCodesToBuildClaim.SessionProcedureCode
                        ? sessions.filter((session) => {
                            const minAllowedDate = addWeeks(
                              startOfWeek(subWeeks(new Date(), billingLagInWeeks), {
                                weekStartsOn: 0,
                              }),
                              1,
                            );
                            return moment(session.date)
                              .tz(CT_TIMEZONE_AREA)
                              .isBefore(minAllowedDate);
                          })
                        : sessions
                    }
                    columns={ClaimSessionTableColumns(
                      isSelectedAll && selectedTableKey === compositeKey,
                      true,
                      (selected: boolean) =>
                        handleSelectAll(
                          selected,
                          compositeKey,
                          bayValue,
                          clinicalRelationship,
                          monthKey.split('-')[0] ?? null,
                          monthKey.split('-')[1] ?? null,
                          providerId,
                        ),
                      false,
                      selectedProcedureCode?.code ===
                        AllowedServiceCodesToBuildClaim.SessionProcedureCode,
                      providerLicensesLoading || providerPayorCredentialsLoading,
                    )}
                    options={ClaimSessionTableOptions(
                      true,
                      selectedProcedureCode.code,
                      (selected: boolean, session: ISessionWithClaimStatus) => {
                        handleSelectRow(
                          selected,
                          session,
                          compositeKey,
                          bayValue,
                          clinicalRelationship,
                          monthKey.split('-')[0] ?? null,
                          monthKey.split('-')[1] ?? null,
                          providerId,
                        );
                      },
                      false,
                      selectedPolicyDetails,
                    )}
                  />
                </Box>
              );
            })}
          </Box>
        </Box>
      )}
    </Box>
  );
}

export default ClaimBuilder;
